// @ts-nocheck
import * as React from "react";
import * as V from "../../village/api";
import { TOKENS, ToastProvider } from "../../village/ui";
import { AppFooter, BRAND_IMG, NCARE, Sidebar, Topbar, siteRegion } from "../common";
import { OverviewPanel } from "../../village/panels/overview";
import { MembersPanel } from "../../village/panels/members";
import { YouthsPanel } from "../../village/panels/youths";
import { ProgramsPanel } from "../../village/panels/programs";
import { SafetyWatchesPanel } from "../../village/panels/safety-watches";
import { MotionsPanel } from "../../village/panels/motions";
import { TeachersPanel } from "../../village/panels/teachers";
import { CurriculaPanel } from "../../village/panels/curricula";
import { MapPanel } from "../../village/panels/map";

// 마을교육공동체(돌봄연계국 C) 셸.
//
// 운영자(system_admin / region_admin / coordinator / agency)로 로그인한 경우에는
// 통합돌봄의 공용 Sidebar + Topbar 셸 안에서 Village 를 "모듈" 로 노출해 '다른
// 사이트' 느낌을 없앤다 — 사이드바에서 다른 운영 메뉴로 바로 이동 가능.
// 비로그인 또는 citizen/guardian 사용자는 사이드바가 없으므로 slim 셸로 폴백.
//
// Phase 0 placeholder("Phase 0 · 모듈 활성 확인") 및 "남양주 마을교육공동체" 제목은
// 두 모드 모두에서 섹션 서브헤더에 보존(E2E 호환).

// Village 서브 브랜드 녹색(돌봄연계국 C). 뱃지에만 아주 좁게 얹는다.
const VILLAGE_GREEN = { main: "#4F8A72", soft: "#D7E5CA", deep: "#2F5A3E" };

type TabKey =
  | "overview" | "members" | "youths" | "programs"
  | "safety" | "motions" | "teachers" | "curricula" | "map";

interface Tab {
  key: TabKey; label: string;
  publicView?: boolean;
  operatorOnly?: boolean;
  render: (ctx: PanelCtx) => React.ReactNode;
}

interface PanelCtx {
  user: any | null;
  isOperator: boolean;
  bureaus: V.Bureau[];
}

const TABS: Tab[] = [
  { key: "overview",  label: "7국 개요", publicView: true,
    render: (c) => <OverviewPanel bureaus={c.bureaus} user={c.user} /> },
  { key: "members",   label: "내 소속·승인",
    render: (c) => <MembersPanel bureaus={c.bureaus} isOperator={c.isOperator} /> },
  { key: "youths",    label: "청소년", operatorOnly: true,
    render: (c) => <YouthsPanel user={c.user} /> },
  { key: "programs",  label: "마을학교(PBL)",
    render: (c) => <ProgramsPanel bureaus={c.bureaus} isOperator={c.isOperator} user={c.user} /> },
  { key: "safety",    label: "민보 안전망",
    render: (c) => <SafetyWatchesPanel isOperator={c.isOperator} /> },
  { key: "motions",   label: "공의 의회",
    render: (c) => <MotionsPanel isOperator={c.isOperator} user={c.user} /> },
  { key: "teachers",  label: "마을교사",
    render: (c) => <TeachersPanel bureaus={c.bureaus} isOperator={c.isOperator} /> },
  { key: "curricula", label: "토의 교육과정",
    render: (c) => <CurriculaPanel bureaus={c.bureaus} isOperator={c.isOperator} /> },
  { key: "map",       label: "지도",
    render: (c) => <MapPanel isOperator={c.isOperator} /> },
];

// 운영자 역할 — 통합돌봄 공용 Sidebar 가 성립하는 역할군. agency 는 권역 없이도
// 사이드바 자체(기관 스코프)는 사용하므로 포함한다. citizen/guardian 은 평상시
// GuardianTabBar/모바일 UI 를 쓰므로 Village 에서는 slim 셸로 폴백.
const STAFF_WITH_SIDEBAR = ["system_admin", "region_admin", "coordinator", "agency"];

export function VillageShell() {
  return (
    <ToastProvider>
      <VillageShellInner />
    </ToastProvider>
  );
}

function VillageShellInner() {
  const [user, setUser] = React.useState<any>(null);
  const [userLoaded, setUserLoaded] = React.useState(false);
  const [bureaus, setBureaus] = React.useState<V.Bureau[]>([]);
  const [bureausLoaded, setBureausLoaded] = React.useState(false);
  const [tab, setTab] = React.useState<TabKey>(() => readTabFromHash());

  React.useEffect(() => {
    fetch("/api/v1/sessions/me", { credentials: "same-origin" })
      .then((r) => r.ok ? r.json() : null)
      .then((u) => { setUser(u); setUserLoaded(true); })
      .catch(() => setUserLoaded(true));
  }, []);

  React.useEffect(() => {
    V.bureaus.list()
      .then((r) => { setBureaus(r.items); setBureausLoaded(true); })
      .catch(() => setBureausLoaded(true));
  }, []);

  React.useEffect(() => {
    const onHash = () => setTab(readTabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  React.useEffect(() => {
    if (window.location.hash.replace(/^#/, "") !== tab) {
      window.history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);

  const isOperator = V.isOperator(user?.role);
  const useStaffShell = userLoaded && user && STAFF_WITH_SIDEBAR.includes(user.role);
  const visibleTabs = TABS.filter((t) => {
    if (t.publicView) return true;
    if (!user) return false;
    if (t.operatorOnly) return isOperator;
    return true;
  });
  const activeTab = visibleTabs.find((t) => t.key === tab) || visibleTabs[0];

  const panelBody = (
    !bureausLoaded ? (
      <div style={{ padding: 32, textAlign: "center", color: TOKENS.ink3 }}>불러오는 중…</div>
    ) : activeTab ? (
      activeTab.render({ user, isOperator, bureaus })
    ) : null
  );

  // ── 운영자: 공용 Sidebar(Village 전용 메뉴) + Topbar 셸 ──
  //   사이드바가 섹션 네비(7국 개요·소속·청소년 등) 역할을 하므로
  //   가로 TabBar 는 생략. 활성 탭 하이라이트는 Sidebar 가 담당.
  if (useStaffShell) {
    return (
      <div className="app" style={{ height: "100vh" }}>
        <div className="shell">
          <Sidebar role="village" active={activeTab?.key} />
          <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Topbar title={`마을교육공동체 · ${activeTab?.label || ""}`}
                    sub="돌봄연계국 C · 통합돌봄 모듈" />
            <div className="main" style={{ overflow: "auto" }}>
              <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 16px 48px" }}>
                <SectionIntro />
                <div style={{ paddingTop: 16 }}>{panelBody}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── 공개/보호자/어르신: slim 셸 (사이드바 없음) — 가로 탭 바로 섹션 전환 ─
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      background: TOKENS.bg,
      fontFamily: '"Pretendard Variable", Pretendard, system-ui, sans-serif',
      color: TOKENS.ink1,
    }}>
      <SlimHeader user={user} userLoaded={userLoaded} />
      <main style={{ flex: 1, maxWidth: 1120, width: "100%", margin: "0 auto" }}>
        <SectionIntro />
        <TabBar tabs={visibleTabs} active={activeTab?.key} onSelect={setTab} />
        <div style={{ padding: "20px 16px 48px" }}>{panelBody}</div>
      </main>
      <AppFooter />
    </div>
  );
}

// 섹션 서브헤더 — Phase 0 뱃지 + h1 ("남양주 마을교육공동체") 를 두 셸 공통으로.
function SectionIntro() {
  return (
    <div style={{ padding: "16px 0 8px" }}>
      <div style={{
        display: "inline-block", padding: "3px 10px", borderRadius: 999,
        background: VILLAGE_GREEN.soft, color: VILLAGE_GREEN.deep,
        fontSize: 11, fontWeight: 700, marginBottom: 8,
      }}>
        Phase 0 · 모듈 활성 확인 · 돌봄연계국 C
      </div>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: TOKENS.ink1, margin: "0 0 4px" }}>
        남양주 마을교육공동체
      </h1>
      <p style={{ fontSize: 12.5, lineHeight: 1.6, color: TOKENS.ink3, margin: 0 }}>
        돌봄연계국(C) 전산 백본으로 통합돌봄 시스템과 결합된 모듈 · 7개 교육국·4개 권역·6대 아젠다
      </p>
    </div>
  );
}

// 비로그인/보호자용 상단 바 (사이드바 없는 slim 셸 전용).
function SlimHeader({ user, userLoaded }: { user: any | null; userLoaded: boolean }) {
  const region = siteRegion();
  const cityName = (region.name || "").replace(/시$/, "");
  return (
    <header style={{ borderBottom: `1px solid ${TOKENS.border}`, background: TOKENS.surface }}>
      <div style={{
        maxWidth: 1120, margin: "0 auto", padding: "14px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      }}>
        <a href="/" aria-label={`${cityName}형 통합돌봄 홈`}
           style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "inherit", minWidth: 0 }}>
          <img src={BRAND_IMG.mark} alt="" width={34} height={34}
               style={{ width: 34, height: 34, borderRadius: 10, objectFit: "cover", display: "block" }}/>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: NCARE.navy, letterSpacing: "-0.01em" }}>
              {cityName}형 통합돌봄
            </div>
            <div style={{ fontSize: 10, color: NCARE.accent, letterSpacing: "0.1em", fontWeight: 700 }}>
              N-CARE 2.0
            </div>
          </div>
          <span style={{
            marginLeft: 8, padding: "4px 10px", borderRadius: 999,
            background: VILLAGE_GREEN.soft, color: VILLAGE_GREEN.deep,
            fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
          }}>마을교육공동체 · 돌봄연계국 C</span>
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <a href="/" style={{
            fontSize: 12, color: TOKENS.ink2, textDecoration: "none",
            padding: "6px 10px", borderRadius: 8,
          }}>← 통합돌봄 홈</a>
          {userLoaded && user ? (
            <a href="/account" title={user.name}
               style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, color: TOKENS.ink1 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 999, background: "linear-gradient(135deg,#EFB287,#E8825B)",
                color: "#fff", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700,
              }}>{(user.name || "?")[0]}</div>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{user.name}</span>
            </a>
          ) : userLoaded ? (
            <a href="/village/login" style={{
              padding: "7px 12px", borderRadius: 8, background: TOKENS.orange500,
              color: "#fff", fontSize: 12, fontWeight: 700, textDecoration: "none",
            }}>로그인</a>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function TabBar({ tabs, active, onSelect }: {
  tabs: Tab[];
  active: TabKey | undefined;
  onSelect: (k: TabKey) => void;
}) {
  return (
    <nav role="tablist" aria-label="마을교육공동체 섹션" style={{
      borderBottom: `1px solid ${TOKENS.border}`,
      position: "sticky", top: 0, zIndex: 5, background: TOKENS.bg,
    }}>
      <div style={{
        display: "flex", gap: 2, overflowX: "auto", scrollbarWidth: "thin",
      }}>
        {tabs.map((t) => {
          const on = t.key === active;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={on}
              onClick={() => onSelect(t.key)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "10px 14px", fontSize: 13, fontWeight: 600,
                color: on ? TOKENS.orange600 : TOKENS.ink2,
                borderBottom: `2px solid ${on ? TOKENS.orange500 : "transparent"}`,
                whiteSpace: "nowrap", fontFamily: "inherit",
              }}
            >{t.label}</button>
          );
        })}
      </div>
    </nav>
  );
}

function readTabFromHash(): TabKey {
  const h = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#/, "");
  const allowed: TabKey[] = ["overview","members","youths","programs","safety","motions","teachers","curricula","map"];
  return (allowed.includes(h as TabKey) ? h : "overview") as TabKey;
}
